import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, ArrowDownTrayIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useDirectory, useSuggestEdit } from '../hooks/useDirectory';
import SuggestEditModal from '../components/SuggestEditModal';
import SuggestionsQueue from '../components/SuggestionsQueue';

// Company directory — one view over the systems that already own the data:
// people (BrightHR x Entra), the Ops "Match Us" sheet, and DOBs.
//
// Columns are driven by what the SERVER sent, not by checking the user's groups
// here. Tech decides visibility and simply omits fields the caller isn't
// entitled to, so this screen renders whatever arrived and cannot accidentally
// reveal — or wrongly hide — a column.

// Presentation only: labels and widths for fields that may or may not be
// present. Anything the server sends that isn't listed still renders, using a
// humanised version of its key, so a new field is never silently dropped.
const COLUMN_LABELS = {
  display_name: 'Name',
  email: 'Email',
  job_title: 'Job title',
  department: 'Department',
  // "Region N East Midlands" -> "East Midlands". It's the area; the ISP column
  // already says which region.
  region: 'Area',
  cluster: 'Cluster',
  isp: 'ISP',
  reports_to: 'Reports to',
  banding: 'Banding',
  target: 'Target',
  live_on_capacity: 'On capacity',
  van_type: 'Van type',
  nops_card_status: 'NOPS',
  nops_expiry: 'NOPS expiry',
  // "Contact number", not "Contact" — someone scanning for a phone number
  // shouldn't have to work out that a column called Contact is one.
  contact_number: 'Contact number',
  stock_count_not_required: 'Stock count exempt',
  date_of_birth: 'Date of birth',
  post_code: 'Post code',
  employment_start_date: 'Started',
  brighthr_id: 'BrightHR id',
  account_enabled: 'Account enabled',
  left_bright_at: 'Left BrightHR',
};

// entra_user_id is the join key, not information — it's returned so rows have a
// stable identity, but showing a GUID in a directory helps nobody.
const HIDDEN = new Set(['entra_user_id']);

// Each source gets its own colour so a glance down the header row shows which
// system owns which column. NEO2 is deliberately the odd one out — it's the
// only source we can actually fix here.
const SOURCE_STYLES = {
  'Microsoft 365': 'bg-sky-50 text-sky-700 ring-sky-600/20',
  BrightHR: 'bg-violet-50 text-violet-700 ring-violet-600/20',
  'OPS': 'bg-amber-50 text-amber-800 ring-amber-600/20',
  Nano: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  // Legacy value: rows stored before the rename still say NEO2.
  NEO2: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
};

// Carries the source colour down the whole column, not just the header badge.
// With 23 columns the badge alone means re-reading the header every time you
// look at a value; a coloured edge on each cell makes "which system owns this"
// readable while scanning a row.
//
// A left BORDER rather than a background tint on purpose: 23 tinted columns
// turns the table into a rainbow and makes the actual values harder to read.
// The border is a thin edge that groups without shouting. Full literal class
// strings so Tailwind's scanner keeps them — building them dynamically
// (`border-l-${colour}-400`) would get them purged and silently render nothing.
const SOURCE_BORDERS = {
  'Microsoft 365': 'border-l-2 border-l-sky-400',
  BrightHR: 'border-l-2 border-l-violet-400',
  'OPS': 'border-l-2 border-l-amber-400',
  Nano: 'border-l-2 border-l-emerald-400',
  NEO2: 'border-l-2 border-l-emerald-400',
};

const borderFor = (sources, column) => SOURCE_BORDERS[sources[column]?.source] || '';

const humanise = (key) => key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// Columns whose meaning is unresolved get a "?" against them, so nobody builds
// on a field that's still being decided. Better to show the doubt than to let a
// column look authoritative while HR and Ops are still agreeing what it is.
const UNDER_REVIEW = {
  department:
    "Under review. This is the Ops sheet's role classification (Accredited Engineer, Civils, Office Support Manager, Leaver) — not a company department, and it can contradict the BrightHR job title. Pending an HR/Ops decision on what a department should be and who owns it.",
};

// The Ops sheet prefixes every area with the ISP ("Region N East Midlands"),
// but ISP already has its own column, so the prefix is repeated noise that also
// splits one area into two filter entries. Stripped for display only — the
// stored value keeps the prefix, since that's what Ops maintain and what the
// sync writes back.
const AREA_PREFIX = /^Region\s+[NB]\s+/i;

// The Ops sheet holds phone numbers in seven different shapes: 07360609769,
// +44 7983 943599, 07983 943599, +447983943599, 7983943599 (leading zero lost,
// almost certainly to a spreadsheet treating it as a number), plus "N/A" and
// "Teams".
//
// Normalised for DISPLAY only — the stored value is whatever Ops typed, and
// rewriting their sheet from here would fight the sync. Anything that isn't
// recognisably a UK number is passed through untouched rather than mangled:
// "Teams" is a real answer to "how do I contact them", and silently discarding
// it would be worse than showing it.
function formatPhone(raw) {
  if (typeof raw !== 'string') return raw;
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (/^n\/?a$/i.test(trimmed)) return '—';

  // South African numbers, for the SA office. Kept in +27 form because they're
  // dialled internationally — a leading 0 would be wrong from the UK, and it
  // was a UK-only validator that made two of these look "malformed" when they
  // were simply written locally. Grouped +27 xx xxx xxxx, the SA convention.
  const za = trimmed.replace(/[\s()-]/g, '');
  if (/^\+27\d{9}$/.test(za)) {
    const n = za.slice(3);
    return `+27 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`;
  }
  // Any other international number is shown as stored rather than guessed at —
  // grouping rules differ per country and a wrong grouping reads as a typo.
  if (/^\+(?!44)\d{6,}$/.test(za)) return za;

  let digits = trimmed.replace(/[\s()-]/g, '');
  if (digits.startsWith('+44')) digits = `0${digits.slice(3)}`;
  else if (digits.startsWith('0044')) digits = `0${digits.slice(4)}`;
  else if (/^44[17]\d{9}$/.test(digits)) digits = `0${digits.slice(2)}`;
  // 10 digits beginning 7 is a mobile that lost its leading zero.
  if (/^7\d{9}$/.test(digits)) digits = `0${digits}`;

  // Only UK mobiles are regrouped, as 07xxx xxxxxx. Landlines are deliberately
  // left as typed: area codes vary in length (0161 496 0000 vs 01204 123456),
  // so a blanket 5+6 split produces "01614 960000" — consistent, and wrong.
  // Nearly every number here is a mobile, so the gain isn't worth misformatting
  // the rest.
  if (!/^07\d{9}$/.test(digits)) return trimmed;
  return `${digits.slice(0, 5)} ${digits.slice(5)}`;
}

const displayOf = (key, raw) => {
  if (key === 'contact_number') return formatPhone(raw);
  // A column under review shows "?" in every row, not just a flag on the
  // header. Displaying the Ops sheet's role values under a heading called
  // "Department" would have people quoting them as departments before anyone
  // has decided what a department is — and undoing that is much harder than
  // never starting it. The underlying value is still returned by the API, so
  // this reverses the moment HR and Ops agree.
  if (UNDER_REVIEW[key]) return '?';
  return key === 'region' && typeof raw === 'string' ? raw.replace(AREA_PREFIX, '') : raw;
};

function formatValue(key, raw) {
  const value = displayOf(key, raw);
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'date_of_birth' || key === 'employment_start_date' || key === 'nops_expiry' || key === 'left_bright_at') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-GB');
  }
  if (key === 'account_enabled' || key === 'stock_count_not_required') return value ? 'Yes' : 'No';
  return String(value);
}

function toCsv(rows, columns) {
  const escape = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    // Quote if the value could break the column structure. Names with commas
    // and free-text notes are common enough that skipping this corrupts exports.
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => escape(COLUMN_LABELS[c] || humanise(c))).join(',');
  // Exports what's on screen, not the raw column — someone comparing a
  // spreadsheet against the page shouldn't find different values in it.
  const body = rows.map((r) => columns.map((c) => escape(displayOf(c, r[c]))).join(',')).join('\n');
  return `${header}\n${body}`;
}

function Directory() {
  const { data, isLoading, error } = useDirectory();
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('all');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const suggestEdit = useSuggestEdit();

  // Memoised because the `|| []` fallbacks would otherwise produce a new array
  // identity on every render, which would make the filter memo below re-run
  // constantly — pointless work on a 246-row table with ~22 columns.
  const people = useMemo(() => data?.people || [], [data]);
  const columns = useMemo(() => (data?.fields || []).filter((f) => !HIDDEN.has(f)), [data]);
  const sources = data?.sources || {};
  const freshness = data?.freshness || {};

  // Built from displayed values, so Region N Yorkshire and Region B Yorkshire
  // collapse into one "Yorkshire" entry — filtering by area then spans both
  // ISPs, which is the point of separating the two columns.
  const options = (key) =>
    [...new Set(people.map((p) => displayOf(key, p[key])).filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b))
    );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return people.filter((p) => {
      if (region !== 'all' && displayOf('region', p.region) !== region) return false;
      if (!term) return true;
      // Searches every visible column rather than a fixed few — people look
      // colleagues up by whatever they happen to know: a van reg, a cluster, a
      // manager's name. Matches what's shown, so searching "Yorkshire" finds
      // rows whose stored value is "Region N Yorkshire".
      //
      // Also matches the RAW value, so a phone number types the way people
      // remember it: "07983943599" finds a row displayed as "07983 943599",
      // and so does the +44 form Ops originally entered.
      return columns.some((c) => {
        const shown = String(displayOf(c, p[c]) ?? '').toLowerCase();
        const stored = String(p[c] ?? '').toLowerCase();
        if (shown.includes(term) || stored.includes(term)) return true;
        // Digits-only comparison for phone-shaped searches, so spacing never
        // decides whether a number is findable.
        const termDigits = term.replace(/\D/g, '');
        return termDigits.length >= 6 && shown.replace(/\D/g, '').includes(termDigits);
      });
    });
  }, [people, columns, search, region]);

  const download = () => {
    const blob = new Blob([toCsv(filtered, columns)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nanofibre-directory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <div className="p-8"><p className="text-sm text-gray-400">Loading directory...</p></div>;
  }

  if (error) {
    return (
      <div className="p-8">
        <p className="text-sm text-rose-700">
          {error.response?.data?.error || 'Could not load the directory.'}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Company directory</h1>
        <p className="mt-1 text-sm text-gray-500">
          {people.length} people, from BrightHR, Microsoft 365 and the Operations sheet.
          {data?.tiers?.operations || data?.tiers?.hr ? ' You can see additional columns for your role.' : ''}
        </p>
      </div>

      {/* Admin-only; renders nothing for everyone else (the API 403s). */}
      <SuggestionsQueue />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[16rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, region, manager..."
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </div>

        {/* No department filter while the column is under review — every row
            reads "?", so the dropdown would offer a single meaningless option.
            It comes back with the column. */}
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        >
          <option value="all">All regions</option>
          {options('region').map((r) => <option key={r} value={r}>{r}</option>)}
        </select>

        <button
          type="button"
          onClick={download}
          disabled={!filtered.length}
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export CSV
        </button>

        <button
          type="button"
          onClick={() => setSuggestOpen(true)}
          className="flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark"
        >
          <PencilSquareIcon className="h-4 w-4" />
          Suggest an edit
        </button>
      </div>

      {/* Source legend with last-sync times. "The data is wrong" and "the data
          is stale" are different problems with different answers, and this
          separates them without opening another screen. */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <span className="text-xs font-medium text-gray-500">Where this data comes from:</span>
        {Object.entries(freshness).map(([system, at]) => (
          <span key={system} className="flex items-center gap-1.5 text-xs">
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${SOURCE_STYLES[system] || ''}`}>
              {system}
            </span>
            <span className="text-gray-500">
              synced {at ? new Date(at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'never'}
            </span>
          </span>
        ))}
        <span className="text-xs text-gray-400">
          Nano displays this data — corrections are made in the owning system.
        </span>
      </div>

      <p className="mb-2 text-xs text-gray-400">
        Showing {filtered.length} of {people.length}
      </p>

      {/* The container is height-capped, not just overflow-x. With 246 rows an
          `overflow-x-auto` scrollbar sits below the fold, so the table scrolls
          sideways but you can never reach the bar to do it. Capping the height
          keeps both scrollbars on screen, and lets the header and the name
          column stay pinned — without which 23 columns means constantly
          scrolling back to remember whose row you're on. */}
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              {columns.map((c) => {
                const origin = sources[c];
                return (
                  <th
                    key={c}
                    // Sticky needs its own background or rows scroll through it.
                    // The name column is pinned on both axes, so it carries a
                    // higher z-index than the rest of the header row.
                    className={`whitespace-nowrap bg-gray-50 px-3 py-2 text-left align-top ${borderFor(sources, c)} sticky top-0 ${
                      c === 'display_name' ? 'left-0 z-20' : 'z-10'
                    }`}
                  >
                    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {COLUMN_LABELS[c] || humanise(c)}
                      {UNDER_REVIEW[c] ? (
                        <span
                          title={UNDER_REVIEW[c]}
                          className="ml-1 cursor-help rounded-full bg-amber-100 px-1.5 text-[11px] font-bold text-amber-800 ring-1 ring-inset ring-amber-600/30"
                        >
                          ?
                        </span>
                      ) : null}
                    </span>
                    {/* The point of this screen when someone says the data is
                        wrong: name the system that owns the value, and where to
                        go and change it. NEO2 only displays most of these. */}
                    {origin ? (
                      <span
                        title={`Owned by ${origin.source} — fix it in ${origin.fixIn}`}
                        className={`mt-1 inline-block cursor-help rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${
                          SOURCE_STYLES[origin.source] || 'bg-gray-50 text-gray-600 ring-gray-500/20'
                        }`}
                      >
                        {origin.source}
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((p) => (
              <tr key={p.entra_user_id} className="group hover:bg-gray-50">
                {columns.map((c) => (
                  <td
                    key={c}
                    // The pinned name cell needs an opaque background of its
                    // own, otherwise columns scroll underneath and show through
                    // it. group-hover keeps it in step with the row highlight.
                    className={`whitespace-nowrap px-3 py-2 ${borderFor(sources, c)} ${
                      c === 'display_name'
                        ? 'sticky left-0 z-10 bg-white font-medium text-gray-900 group-hover:bg-gray-50'
                        : 'text-gray-600'
                    }`}
                  >
                    {formatValue(c, p[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-400">Nobody matches that search.</p>
        ) : null}
      </div>

      {/* Given the full, unfiltered list: you should be able to correct anyone's
          record, not just whoever survives the current search. */}
      <SuggestEditModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        people={people}
        columns={columns}
        columnLabels={COLUMN_LABELS}
        sources={sources}
        displayOf={displayOf}
        onSubmit={(payload) => suggestEdit.mutateAsync(payload)}
      />
    </div>
  );
}

export default Directory;
